import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2Icon, LockIcon, AlertCircleIcon, XIcon } from 'lucide-react';
import { trackAddToCart } from '../utils/metaPixel';
interface UpsellPopupProps {
  isOpen: boolean;
  onClose: () => void;
}
export function UpsellPopup({
  isOpen,
  onClose
}: UpsellPopupProps) {
  const FULL_BUNDLE_URL = 'https://amigospeludosarg.shop/cart/49011365642482:1';
  const MAIN_PRODUCT_URL = 'https://amigospeludosarg.shop/cart/48933114577138:1';
  const handleAccept = () => {
    trackAddToCart();
    window.open(FULL_BUNDLE_URL, '_blank', 'noopener,noreferrer');
    onClose();
  };
  const handleDecline = () => {
    trackAddToCart();
    window.open(MAIN_PRODUCT_URL, '_blank', 'noopener,noreferrer');
    onClose();
  };
  return <AnimatePresence data-id="element-409">
      {isOpen && <>
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} className="fixed inset-0 bg-[#1C1C1C]/60 backdrop-blur-sm z-50" data-id="element-410" />
          <div className="fixed inset-0 flex items-center justify-center z-50 p-3 md:p-4 pointer-events-none" data-id="element-411">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col overflow-hidden pointer-events-auto relative" data-id="element-412">
              <button onClick={onClose} className="absolute top-3 right-3 text-white/80 hover:text-white z-10 p-1" data-id="element-413">
                <XIcon className="w-5 h-5" data-id="element-414" />
              </button>

              {/* Header Banner - compact on mobile */}
              <div className="bg-[#D97706] px-4 py-3 md:px-6 md:py-5 text-center relative overflow-hidden flex-shrink-0" data-id="element-415">
                <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-10" data-id="element-416"></div>
                <div className="inline-flex items-center gap-1.5 bg-white/20 text-white text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full mb-2 md:mb-3" data-id="element-417">
                  <AlertCircleIcon className="w-3 h-3" data-id="element-418" />
                  Oferta única antes de finalizar
                </div>
                <h2 className="font-['Playfair_Display'] text-xl md:text-3xl font-bold text-white mb-1 md:mb-2 leading-tight" data-id="element-419">
                  ¡Esperá! No te lleves el problema a medias 🛑
                </h2>
                <p className="text-white/90 text-xs md:text-base" data-id="element-420">
                  Estás a un paso de recuperar tu sofá, pero te faltan las
                  herramientas de implementación.
                </p>
              </div>

              {/* Scrollable body */}
              <div className="p-4 md:p-8 overflow-y-auto flex-1 min-h-0" data-id="element-421">
                {/* Body */}
                <div className="text-center mb-4 md:mb-6" data-id="element-422">
                  <div className="inline-block bg-[#FEF3C7] text-[#D97706] font-bold px-3 py-1 md:px-4 md:py-1.5 rounded-full text-xs md:text-sm mb-3 md:mb-4 animate-pulse border border-[#D97706]/20" data-id="element-423">
                    🔥 30% OFF EN TODAS LAS HERRAMIENTAS EXTRA
                  </div>
                  <p className="text-[#1C1C1C] text-sm md:text-base leading-relaxed mb-1 md:mb-2" data-id="element-424">
                    El protocolo te da el sistema, pero el{' '}
                    <strong data-id="element-425">Scanner de Limpieza</strong> y el{' '}
                    <strong data-id="element-426">Calendario de 72 Horas</strong> son los que aseguran
                    que no haya recaídas por olores viejos o señales ignoradas.
                  </p>
                  <p className="text-[#1C1C1C]/70 text-xs md:text-sm" data-id="element-427">
                    Llevate el arsenal completo para resolver esto este mismo
                    fin de semana.
                  </p>
                </div>

                {/* What's Included */}
                <div className="bg-[#FAFAF7] rounded-xl p-3 md:p-5 border border-[#1C1C1C]/10 mb-4 md:mb-6" data-id="element-428">
                  <p className="text-[10px] md:text-xs font-bold text-[#2D8C5A] uppercase tracking-wide mb-2 md:mb-3" data-id="element-429">
                    Tu Pack Completo Total incluye:
                  </p>
                  <div className="space-y-1.5 md:space-y-2.5" data-id="element-430">
                    <div className="flex items-start gap-2" data-id="element-431">
                      <CheckCircle2Icon className="w-3.5 h-3.5 md:w-4 md:h-4 text-[#2D8C5A] flex-shrink-0 mt-0.5" data-id="element-432" />
                      <span className="text-xs md:text-sm text-[#1C1C1C]" data-id="element-433">
                        <strong data-id="element-434">El Protocolo de 72 Horas</strong> + Los 3 Bonos
                        Gratuitos
                      </span>
                    </div>
                    <div className="flex items-start gap-2" data-id="element-435">
                      <CheckCircle2Icon className="w-3.5 h-3.5 md:w-4 md:h-4 text-[#D97706] flex-shrink-0 mt-0.5" data-id="element-436" />
                      <span className="text-xs md:text-sm text-[#1C1C1C]" data-id="element-437">
                        <strong data-id="element-438">Scanner de Limpieza:</strong> Rompé la molécula
                        del olor para que no vuelva a marcar.
                      </span>
                    </div>
                    <div className="flex items-start gap-2" data-id="element-439">
                      <CheckCircle2Icon className="w-3.5 h-3.5 md:w-4 md:h-4 text-[#D97706] flex-shrink-0 mt-0.5" data-id="element-440" />
                      <span className="text-xs md:text-sm text-[#1C1C1C]" data-id="element-441">
                        <strong data-id="element-442">Calendario 72 Horas:</strong> Anticipá el
                        accidente leyendo las señales a tiempo.
                      </span>
                    </div>
                  </div>
                </div>

                {/* Pricing Block */}
                <div className="text-center mb-4 md:mb-6" data-id="element-443">
                  <div className="flex items-center justify-center gap-2 md:gap-3 mb-1" data-id="element-444">
                    <span className="text-[#1C1C1C]/50 line-through text-base md:text-lg font-medium" data-id="element-445">
                      $20.999
                    </span>
                    <span className="text-2xl md:text-4xl font-['Playfair_Display'] font-bold text-[#2D8C5A]" data-id="element-446">
                      $17.399
                    </span>
                  </div>
                  <div className="flex items-center justify-center gap-1.5 text-[10px] md:text-xs text-[#1C1C1C]/60 font-medium" data-id="element-447">
                    <LockIcon className="w-3 h-3" data-id="element-448" />
                    Precio especial disponible solo en esta pantalla
                  </div>
                </div>

                {/* CTAs */}
                <div className="space-y-2 md:space-y-3" data-id="element-449">
                  <button onClick={handleAccept} className="w-full bg-[#D97706] text-white font-bold px-4 py-3 md:px-6 md:py-4 rounded-xl text-sm md:text-lg hover:bg-[#B45309] transition-all shadow-[0_0_20px_rgba(217,119,6,0.4)] hover:shadow-[0_0_25px_rgba(217,119,6,0.6)] relative overflow-hidden group" data-id="element-450">
                    <div className="absolute inset-0 bg-white/20 translate-y-full group-hover:translate-y-0 transition-transform duration-300 ease-out" data-id="element-451"></div>
                    <span className="relative" data-id="element-452">
                      SÍ! QUIERO EL PACK COMPLETO POR $17.399
                    </span>
                  </button>
                  <button onClick={handleDecline} className="w-full text-[#1C1C1C]/50 font-medium px-4 py-2 md:px-6 md:py-3 text-xs md:text-sm hover:text-[#1C1C1C]/80 transition-colors" data-id="element-453">
                    No gracias, continuar sin los extras →
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        </>}
    </AnimatePresence>;
}